import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  Output,
  Renderer2,
} from '@angular/core';

/**
 * Dismiss an overlay only when press and release both land on the host
 * (the dimmed backdrop). A hold that starts inside the dialog — including
 * when the dialog is clipped off-screen — does not count as click-outside.
 */
@Directive({
  selector: '[portalBackdropDismiss]',
  standalone: true,
})
export class PortalBackdropDismissDirective implements OnDestroy {
  @Output() readonly portalBackdropDismiss = new EventEmitter<void>();

  private armed = false;
  private releasedOnHost = false;
  private unlistenUp: (() => void) | null = null;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
  ) {}

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      this.reset();
      return;
    }
    this.armed = event.target === this.host.nativeElement;
    this.releasedOnHost = false;
    this.unlistenDocUp();
    this.unlistenUp = this.renderer.listen('document', 'pointerup', (up: PointerEvent) => {
      this.releasedOnHost = this.armed && up.target === this.host.nativeElement;
      this.unlistenDocUp();
    });
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (this.armed && this.releasedOnHost && event.target === this.host.nativeElement) {
      this.portalBackdropDismiss.emit();
    }
    this.reset();
  }

  ngOnDestroy(): void {
    this.reset();
  }

  private reset(): void {
    this.armed = false;
    this.releasedOnHost = false;
    this.unlistenDocUp();
  }

  private unlistenDocUp(): void {
    this.unlistenUp?.();
    this.unlistenUp = null;
  }
}
